/**
 * Drag-identity-mismatch detector for the pipeline board.
 *
 * Detects a drag that dnd-kit resolved to a different card than the one
 * under the pointer. Extracted from PipelineBoard — see `handleDragStart`
 * there for the call site.
 */

import type { DragStartEvent } from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import * as Sentry from '@sentry/react';

import { logger } from '@/lib/logger';

/**
 * Session-scoped dedup for {@link reportDragIdentityMismatch}, keyed by
 * `activeId:domRowId:resolvedInMap`. The key includes `resolvedInMap` because a
 * stale-registry mismatch and a stale-map mismatch on the same card pair are
 * distinct signals and must not suppress each other. Module-level (not
 * component state) so it survives remounts within the same page session; reset
 * only by tests.
 */
const reportedDragIdentityMismatches = new Set<string>();

/** Test-only: clears the dedup state so tests don't depend on run order. */
export function __resetDragIdentityMismatchDedupForTesting(): void {
  reportedDragIdentityMismatches.clear();
}

/**
 * Detects a drag that dnd-kit resolved to a different card than the one under
 * the pointer.
 *
 * The known cause of this — the board reflowing on a timer after a drop, so a
 * user's next click landed on a card the reflow had slid under their cursor —
 * has been removed (see the note above RESORT_NOTICE_MS in PipelineBoard).
 * This detector stays as a tripwire, because it does not depend on knowing
 * WHY the DOM moved: any future source of movement between `mousedown` and
 * activation shows up here the same way.
 *
 * ⚠️ It reports via `Sentry.captureMessage`, which is a NO-OP unless
 * `VITE_SENTRY_DSN` is non-empty at build time. Both frontend deploy workflows
 * now pass that variable through, but it resolves from the `VITE_SENTRY_DSN`
 * repository secret — so this reports somewhere only once that secret exists.
 * Until then the SDK stays inert and this detector's silence still means
 * nothing. `main.tsx` logs a console warning on any non-development build with
 * no DSN, so check the browser console before reading silence as "no
 * mismatches".
 *
 * This is DETECTION ONLY — it changes no behaviour. It compares dnd-kit's
 * `active.id` against the `data-row-id` of whatever is genuinely under the
 * activating pointer, and reports the disagreement with both ids plus the
 * rendered order, which is enough to tell a stale sortable registry apart from a
 * stale `itemByIdMap`.
 *
 * Best-effort by design: keyboard drags carry no coordinates, and a pointer that
 * travels past the activation threshold onto a neighbouring card can report a
 * benign mismatch — so treat a single report as a lead, not a verdict.
 *
 * Deduped per session by exact repeat (see {@link reportedDragIdentityMismatches})
 * so that if the underlying bug turns out to be repeatable, it cannot generate
 * unbounded Sentry events — a distinct mismatch (different ids or resolution
 * state) is never suppressed by a prior, different one.
 */
export function reportDragIdentityMismatch(
  event: DragStartEvent,
  activeId: string,
  resolvedInMap: boolean
): void {
  const coords = getEventCoordinates(event.activatorEvent);
  if (!coords) {
    return; // Keyboard drag — no pointer to compare against.
  }

  const domRowId = document
    .elementFromPoint(coords.x, coords.y)
    ?.closest('[data-row-id]')
    ?.getAttribute('data-row-id');

  if (!domRowId || domRowId === activeId) {
    return;
  }

  const dedupKey = `${activeId}:${domRowId}:${resolvedInMap}`;
  if (reportedDragIdentityMismatches.has(dedupKey)) {
    return;
  }
  reportedDragIdentityMismatches.add(dedupKey);

  // Always mirror into the logger ring buffer: the Sentry report below is a
  // no-op with no DSN configured (see the function doc), so without this the
  // signal is unobservable in the field. `window.__ahoy.logs()` surfaces it.
  logger.warn(
    'pipeline-dnd: drag identity mismatch — dnd-kit active.id differs from card under pointer',
    { activeId, domRowIdUnderPointer: domRowId, resolvedInMap }
  );

  try {
    Sentry.captureMessage('pipeline.drag.identity_mismatch', {
      level: 'warning',
      extra: {
        activeId,
        domRowIdUnderPointer: domRowId,
        resolvedInMap,
        renderedOrder: Array.from(
          document.querySelectorAll('[data-row-id]'),
          el => el.getAttribute('data-row-id')
        ),
      },
    });
  } catch (error) {
    logger.warn(
      'Sentry SDK failed while reporting drag identity mismatch (not a drag bug)',
      {
        error: error instanceof Error ? error.message : String(error),
        activeId,
        domRowId,
      }
    );
  }
}
